import { useState } from 'react';
import Layout from '@/components/Layout';
import { PropertyCard } from '@/components/PropertyCard';
import { PropertyFilters } from '@/components/PropertyFilters';
import { useProperties, PropertyFilters as PropertyFiltersType } from '@/hooks/useProperties';
import { Skeleton } from '@/components/ui/skeleton';
import { Building2, TrendingUp, Shield, Globe, MapPin, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const RealEstate = () => {
  const [filters, setFilters] = useState<PropertyFiltersType>({});
  const [searchQuery, setSearchQuery] = useState('');
  const { data: properties, isLoading } = useProperties(filters);

  const filteredProperties = properties?.filter(property =>
    property.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    property.location_city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    property.location_province?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const featuredProperties = properties?.filter(p => p.is_featured).slice(0, 3);

  const handleResetFilters = () => {
    setFilters({});
    setSearchQuery('');
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center justify-center bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('/hero-beach.jpg')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/50 to-primary" />
        
        <div className="container mx-auto px-4 relative z-10 text-center py-20">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-in">
            Find Your Dream Property
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-white/90 max-w-3xl mx-auto">
            Discover luxury real estate in the heart of the Dominican Republic
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-2xl p-2 flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by location or property name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 border-0 focus-visible:ring-0 text-foreground"
              />
            </div>
            <Button size="lg" className="px-8">
              Search
            </Button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Filters Sidebar */}
            <div className="lg:col-span-1">
              <PropertyFilters onFilterChange={setFilters} onReset={handleResetFilters} />
            </div>

            {/* Properties Grid */}
            <div className="lg:col-span-3">
              {/* Results Header */}
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold">
                  {isLoading ? (
                    'Loading properties...'
                  ) : (
                    `${filteredProperties?.length || 0} Properties Found`
                  )}
                </h2>
              </div>

              {/* Properties Grid */}
              {isLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="space-y-3">
                      <Skeleton className="h-64 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  ))}
                </div>
              ) : filteredProperties && filteredProperties.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredProperties.map((property) => (
                    <PropertyCard key={property.id} property={property} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <Building2 className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-2xl font-semibold mb-2">No Properties Found</h3>
                  <p className="text-muted-foreground mb-6">
                    Try adjusting your filters or search criteria
                  </p>
                  <Button onClick={handleResetFilters}>Reset Filters</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      {featuredProperties && featuredProperties.length > 0 && (
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Featured Properties</h2>
              <p className="text-muted-foreground text-lg">
                Discover our most exclusive listings
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProperties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why Invest in Dominican Republic */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Why Invest in Dominican Republic?</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Discover the advantages of owning property in this Caribbean paradise
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6 rounded-lg bg-gradient-to-br from-primary/10 to-transparent border border-primary/20 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Globe className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Tourism Paradise</h3>
              <p className="text-muted-foreground">
                One of the Caribbean's top tourist destinations with year-round visitors
              </p>
            </div>

            <div className="text-center p-6 rounded-lg bg-gradient-to-br from-primary/10 to-transparent border border-primary/20 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <TrendingUp className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Growing Economy</h3>
              <p className="text-muted-foreground">
                Strong economic growth and increasing property values
              </p>
            </div>

            <div className="text-center p-6 rounded-lg bg-gradient-to-br from-primary/10 to-transparent border border-primary/20 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Shield className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Tax Benefits</h3>
              <p className="text-muted-foreground">
                Attractive tax incentives for foreign investors
              </p>
            </div>

            <div className="text-center p-6 rounded-lg bg-gradient-to-br from-primary/10 to-transparent border border-primary/20 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <MapPin className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Beach Lifestyle</h3>
              <p className="text-muted-foreground">
                Beautiful beaches, perfect weather, and vibrant culture
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Find Your Perfect Property?
          </h2>
          <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
            Our team of real estate experts is here to help you every step of the way
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="text-lg px-8">
              Contact Us
            </Button>
            <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 hover:bg-white/20 text-white border-white">
              Schedule a Viewing
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default RealEstate;
